import QuizQuestion from './QuizQuestion';
import ShellTerminal from './ShellTerminal';
import TextCommandInput from './TextCommandInput';

import type { ExerciseData, SubmissionData } from '../features/exercises/exerciseTypes';

interface ExerciseWorkspaceProps {
  exercise: ExerciseData;
  submission: SubmissionData | null;
  isSubmitting: boolean;
  /** Exercise is finished (solved, or out of attempts): shown greyed out and not interactive. */
  locked?: boolean;
  onSubmitCommand: (command: string) => void | Promise<void>;
  onSubmitQuiz: (selectedOptionIds: string[]) => void | Promise<void>;
}

function OutputComparison({
  exercise,
  submission,
}: {
  exercise: ExerciseData;
  submission: SubmissionData | null;
}) {
  const exerciseType = String(exercise.type ?? exercise.validatorType ?? '').toUpperCase();
  if (!exerciseType.includes('OUTPUT')) {
    return null;
  }

  return (
    <section aria-labelledby="output-comparison-heading" style={{ display: 'grid', gap: '0.75rem' }}>
      <h2 id="output-comparison-heading">Output comparison</h2>
      <div>
        <h3>Challenge</h3>
        <p>{exercise.description}</p>
      </div>
      <div>
        <h3>Your output</h3>
        <pre style={{ whiteSpace: 'pre-wrap' }}>
          {submission?.executionOutput ?? 'Run a command to see its output.'}
        </pre>
      </div>
    </section>
  );
}

function ExerciseInput({
  exercise,
  disabled,
  onSubmitCommand,
  onSubmitQuiz,
}: Pick<ExerciseWorkspaceProps, 'exercise' | 'onSubmitCommand' | 'onSubmitQuiz'> & { disabled: boolean }) {
  const exerciseType = String(exercise.type ?? exercise.validatorType ?? '').toUpperCase();

  if (exerciseType.includes('MULTIPLE') || exerciseType.includes('CHOICE')) {
    return (
      <QuizQuestion
        question={exercise.title}
        options={exercise.options?.length
          ? exercise.options
          : [
              { id: 'a', text: 'ls' },
              { id: 'b', text: 'rm' },
              { id: 'c', text: 'mkdir' },
            ]}
        onSubmit={onSubmitQuiz}
        disabled={disabled}
      />
    );
  }
  if (exerciseType.includes('FILL')) {
    return <TextCommandInput onSubmit={onSubmitCommand} disabled={disabled} />;
  }
  return <ShellTerminal onSubmit={onSubmitCommand} disabled={disabled} />;
}

function ExerciseWorkspace({
  exercise,
  submission,
  isSubmitting,
  locked = false,
  onSubmitCommand,
  onSubmitQuiz,
}: ExerciseWorkspaceProps) {
  return (
    <div
      data-testid="exercise-workspace"
      aria-disabled={locked || undefined}
      style={{
        display: 'grid',
        gap: '1.5rem',
        ...(locked ? { opacity: 0.45, filter: 'grayscale(1)', pointerEvents: 'none' } : {}),
      }}
    >
      <ExerciseInput
        exercise={exercise}
        disabled={isSubmitting}
        onSubmitCommand={onSubmitCommand}
        onSubmitQuiz={onSubmitQuiz}
      />
      <OutputComparison exercise={exercise} submission={submission} />
    </div>
  );
}

export default ExerciseWorkspace;
